import React, { useCallback, useContext, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { UI, ScreenSize } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import { withRouter, RouteComponentProps } from "react-router";
import { RootStoreContext } from "src/stores/RootStore";
import { USER_SETTING } from "src/stores/UserSettingsStore";
import { Role, hasMfaExcludedRole } from "src/network/User";
import { selectRoute, Route } from "src/config/routes";
import { useParams } from "react-router";
import { useQuery } from "@apollo/client";
import { GET_CUSTOMER_USER } from "src/api/customer";
import { GetCustomerUsers } from "src/api/generated/GetCustomerUsers";
import * as Screens from "src/screens";
import CustomerUserList from "src/screens/customerusers/list/details/list/CustomerUserDetailsList";
import listStyles from "src/screens/customerusers/list/details/list/CustomerUserDetailsList.module.css";
import { CustomerUserResetButton } from "./resetButton/CustomerUserResetButton";
import { CopyUserButton } from "./copyUserButton/CopyUserButton";

interface Params {
    userid: any;
}

const CustomerUserDetailsBase = (props: RouteComponentProps) => {
    const { t } = useTranslation();
    const { navStore, uiStore, authStore, userSettingsStore } = useContext(RootStoreContext);
    const { userid } = useParams<Params>();
    const [mfaEnabled, setMfaEnabled] = useState<boolean>(false);
    const [mfaMandatory, setMfaMandatory] = useState<boolean>(false);
    const [isMfaSaving, setIsMfaSaving] = useState<boolean>(false);
    const isAdministrator = authStore.user?.availableRoles?.includes(Role.ADMINISTRATOR) ?? false;

    useEffect(() => {
        navStore.setTitle(t(""));
        navStore.setActionButtonMiddle(null);
        navStore.setActionButtonRight(null);
        navStore.setBackbutton(selectRoute(Route.customerusers), t("screens.customer_users.title").toString());
    }, [navStore, t]);

    useEffect(() => {
        if (!isAdministrator || !navStore.hasFeatureFlagMFA) {
            return;
        }

        // MFA.HASPERMISSION = "false" means MFA is mandatory (the user may not disable it); a missing row or
        // "true" means it is not.
        userSettingsStore
            .loadUserSetting(USER_SETTING.MFA_HASPERMISSION, userid)
            .then((value) => setMfaMandatory(value === "false"))
            .catch(() => setMfaMandatory(false));
    }, [userSettingsStore, userid, isAdministrator, navStore.hasFeatureFlagMFA]);

    useEffect(() => {
        if (uiStore.isCustomerUserReset) {
            refetch();
            uiStore.setIsCustomerUserReset(false);
        }
        // eslint-disable-next-line
    }, [uiStore.isCustomerUserReset, t, uiStore]);

    const { data, loading, error, refetch } = useQuery<GetCustomerUsers>(GET_CUSTOMER_USER, {
        variables: {
            userid: userid
        },
        // The MFA bulk card changes users behind this query's cache entry, so always read the current state.
        fetchPolicy: "network-only"
    });

    const customerUser = data?.customer_users.find((user) => user.userid === userid);

    useEffect(() => {
        if (customerUser) {
            setMfaEnabled(customerUser.mfaenabled);
        }
    }, [customerUser]);

    const onToggleMfa = useCallback(async () => {
        const newMfaEnabled = !mfaEnabled;
        setIsMfaSaving(true);
        setMfaEnabled(newMfaEnabled);

        // userId targets the customer user being edited; the backend checks the caller is an administrator.
        const success = await authStore.authClient.toggleMfa(newMfaEnabled, userid);
        if (!success) {
            setMfaEnabled(!newMfaEnabled);
        }
        setIsMfaSaving(false);
    }, [authStore.authClient, mfaEnabled, userid]);

    const onToggleMfaMandatory = useCallback(async () => {
        const newMandatory = !mfaMandatory;
        setIsMfaSaving(true);

        // Making MFA mandatory also switches MFA on; the mandatory endpoint only stores the setting.
        if (newMandatory && !mfaEnabled) {
            const enabled = await authStore.authClient.toggleMfa(true, userid);
            if (!enabled) {
                setIsMfaSaving(false);
                return;
            }
            setMfaEnabled(true);
        }

        setMfaMandatory(newMandatory);
        const success = await authStore.authClient.setMfaMandatory(newMandatory, userid);
        if (!success) {
            setMfaMandatory(!newMandatory);
        }
        setIsMfaSaving(false);
    }, [authStore.authClient, mfaEnabled, mfaMandatory, userid]);

    // An unregistered user has no login yet, so neither MFA nor "mandatory" can apply to them.
    const isRegistered = customerUser?.registered ?? false;
    // Inspection app users (role 20/21, alone or next to other roles) cannot use MFA.
    const isMfaExcluded = hasMfaExcludedRole(customerUser?.userroles ?? []);

    const mfaSecurityItems = [
        {
            key: t("screens.customer_users.mfa_activate").toString(),
            tooltip: undefined as string | undefined,
            checked: mfaEnabled,
            onToggle: onToggleMfa,
            // While MFA is mandatory the backend refuses to disable it (409), so the switch is locked.
            disabled: isMfaSaving || mfaMandatory || !isRegistered || isMfaExcluded
        },
        {
            key: t("screens.customer_users.mfa_mandatory").toString(),
            tooltip: t("screens.customer_users.mfa_mandatory_tooltip").toString() as string | undefined,
            checked: mfaMandatory,
            onToggle: onToggleMfaMandatory,
            disabled: isMfaSaving || !isRegistered || isMfaExcluded
        }
    ];

    const renderMfaItem = useCallback((item: (typeof mfaSecurityItems)[number], screenSize: ScreenSize) => {
        const label = item.tooltip ? (
            <UI.OverlayTrigger
                placement="top"
                overlay={<UI.Tooltip id={`mfa-tooltip-${item.key}`}>{item.tooltip}</UI.Tooltip>}
            >
                <span>{item.key}</span>
            </UI.OverlayTrigger>
        ) : (
            item.key
        );

        const listRows = [
            <UI.List.Row key={"r-1"}>
                <UI.List.Cell
                    className={listStyles.KeyCell}
                    key={"c-1"}
                    colspan={screenSize === ScreenSize.MOBILE ? 0.5 : 1}
                    value={label}
                />
                <UI.Col className={listStyles.ColCheckbox}>
                    <UI.Switch checked={item.checked} onToggle={item.onToggle} disabled={item.disabled} />
                </UI.Col>
            </UI.List.Row>
        ];

        return <UI.List.Item key={item.key} screenSize={screenSize} rows={listRows} />;
    }, []);

    if (loading) {
        return <Screens.Loading />;
    }
    if (error) {
        return <Screens.Error message={error.message} networkError={error.networkError} />;
    }
    if (!data) {
        return <Screens.Error message={t("error.nodata").toString()} />;
    }

    return (
        <UI.Container className="pt-4">
            <UI.Title>{t("screens.customer_users.user_details")}</UI.Title>
            <UI.Card title={t("screens.customer_users.basic_data")}>
                <CustomerUserList user={customerUser || data.customer_users[0]} />
                <div style={{ display: "flex", gap: "10%" }}>
                    <CustomerUserResetButton user={customerUser || data.customer_users[0]} userid={userid} />
                    {navStore.hasFeatureFlagGhostUser ? (
                        <CopyUserButton user={customerUser || data.customer_users[0]} userid={userid} />
                    ) : undefined}
                </div>
            </UI.Card>
            {isAdministrator && navStore.hasFeatureFlagMFA && (
                <UI.Card title={t("screens.customer_users.security_settings").toString()}>
                    <UI.List.BasicList
                        screenSize={uiStore.isMobile ? ScreenSize.MOBILE : ScreenSize.DESKTOP}
                        items={mfaSecurityItems}
                        renderItem={renderMfaItem}
                    />
                    {isMfaExcluded && (
                        <div className="text-muted mt-2" role="note">
                            {t("screens.customer_users.mfa_inspection_user_warning")}
                        </div>
                    )}
                </UI.Card>
            )}
        </UI.Container>
    );
};

export const CustomerUserDetails = withRouter(Sentry.withProfiler(observer(CustomerUserDetailsBase)));
